import React, { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import Modal from 'react-modal'

import {
  FiArrowLeft,
  FiArrowRight,
  FiMinusCircle,
  FiPlusCircle,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
} from 'react-icons/fi'

import usePersistedState from '../../hooks/usePersistedState'

import Header from '../../components/Header'
import InfoBox from '../../components/InfoBox'
import Button from '../../components/Button'

import {
  Container,
  Content,
  Cards,
  BoxButtons,
  ButtonSimulationCalc,
} from './styles'

const ResultSimulation: React.FC = () => {
  const history = useHistory()
  const [totalBalance] = usePersistedState('totalBalance', 0)
  const [timeValueYears, setTimeValueYears] = usePersistedState<number>(
    'TimeValueYears',
    1,
  )
  const [percentualValuePercent, setPercentualValuePercent] = usePersistedState<
    number
  >('PercentualValuePercent', 0.3)
  const [selectedReceive, setSelectedReceive] = usePersistedState<'PD' | 'PS'>(
    'receiveTypeSelected',
    'PD',
  )

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [titleModal, setTitleModal] = useState('')
  const [textModal, setTextModal] = useState('')

  const titlePercent = 'Percentual sobre o saldo'
  const textPercent = `Valor simulado referente a
    ${parseFloat(percentualValuePercent.toFixed(2))}% do seu saldo projetado,
    com pagamentos mensais efetuados com base no
    percentual que escolher no momento da aposentadoria`
  const titleYears = 'Prazo determinado'
  const textYears = `Valor simulado referente à modalidade de prado determinado
    com duração de ${timeValueYears} anos calculado com base
    no seu saldo projetado`

  const adicPerct = useCallback(() => {
    if (percentualValuePercent >= 2.0) {
      setPercentualValuePercent(2.0)
    } else {
      setPercentualValuePercent(percentualValuePercent + 0.05)
    }
  }, [percentualValuePercent, setPercentualValuePercent])

  const tiraPerct = useCallback(() => {
    if (percentualValuePercent <= 0.25) {
      setPercentualValuePercent(0.25)
    } else {
      setPercentualValuePercent(percentualValuePercent - 0.05)
    }
  }, [percentualValuePercent, setPercentualValuePercent])

  const adicAno = useCallback(() => {
    if (timeValueYears >= 50) {
      setTimeValueYears(50)
    } else {
      setTimeValueYears(timeValueYears + 1)
    }
  }, [setTimeValueYears, timeValueYears])

  const tiraAno = useCallback(() => {
    if (timeValueYears <= 1) {
      setTimeValueYears(1)
    } else {
      setTimeValueYears(timeValueYears - 1)
    }
  }, [setTimeValueYears, timeValueYears])

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const toggleSelectedReceive = useCallback(
    (selectedReceiveNow: 'PD' | 'PS') => {
      setSelectedReceive(selectedReceiveNow)
    },
    [setSelectedReceive],
  )

  const handleConfirmValues = useCallback(() => {
    history.push('/confirm-ownership')
  }, [history])

  const handleChangeValues = useCallback(() => {
    history.push('/simulation')
  }, [history])

  useEffect(() => {
    toggleSelectedReceive(selectedReceive)
  }, [selectedReceive, toggleSelectedReceive])

  return (
    <>
      <Header>
        <FiArrowLeft
          type="button"
          cursor="pointer"
          onClick={handleChangeValues}
        />
      </Header>

      <Container>
        <Content>
          <h3>
            Como você quer <strong>receber sua renda mensal no futuro</strong>?
          </h3>

          <Cards>
            <InfoBox isActive={selectedReceive === 'PS'}>
              <FiCheckCircle
                onClick={() => {
                  toggleSelectedReceive('PS')
                }}
              />
              <h3>Percentual sobre o saldo</h3>
              <BoxButtons>
                <ButtonSimulationCalc type="button" onClick={() => tiraPerct()}>
                  <FiMinusCircle />
                </ButtonSimulationCalc>
                <span>
                  {`${parseFloat(percentualValuePercent.toFixed(2))}%`}
                </span>
                <ButtonSimulationCalc type="button" onClick={() => adicPerct()}>
                  <FiPlusCircle />
                </ButtonSimulationCalc>
              </BoxButtons>
              <small id="value">
                <span>
                  R${' '}
                  {(totalBalance * (percentualValuePercent / 100)).toFixed(2)}
                </span>
                /mês{' '}
                <FiAlertCircle
                  onClick={() => {
                    handleOpenModal()
                    setTitleModal(titlePercent)
                    setTextModal(textPercent)
                  }}
                />
              </small>
            </InfoBox>

            <h3>OU</h3>

            <InfoBox isActive={selectedReceive === 'PD'}>
              <FiCheckCircle
                onClick={() => {
                  toggleSelectedReceive('PD')
                }}
              />
              <h3>Prazo determinado</h3>
              <BoxButtons>
                <ButtonSimulationCalc type="button" onClick={() => tiraAno()}>
                  <FiMinusCircle />
                </ButtonSimulationCalc>
                <span>{`${timeValueYears} ano(s)`}</span>
                <ButtonSimulationCalc type="button" onClick={() => adicAno()}>
                  <FiPlusCircle />
                </ButtonSimulationCalc>
              </BoxButtons>
              <small id="value">
                <span>
                  R$ {(totalBalance / (timeValueYears * 12)).toFixed(2)}
                </span>
                /mês{' '}
                <FiAlertCircle
                  id="years-modal"
                  onClick={() => {
                    handleOpenModal()
                    setTitleModal(titleYears)
                    setTextModal(textYears)
                  }}
                />
              </small>
            </InfoBox>
          </Cards>

          <small>
            Os valores são simulações e não há garantia de rentabilidade futura.
          </small>

          <Button type="button" color="green" onClick={handleConfirmValues}>
            Pronto, é isso que eu quero
            <div>
              <FiArrowRight />
            </div>
          </Button>

          <Modal
            isOpen={isModalOpen}
            onRequestClose={handleCloseModal}
            overlayClassName="react-modal-overlay"
            className="react-modal-content"
          >
            <FiX onClick={handleCloseModal} />
            <h3>{titleModal}</h3>
            <p>{textModal}</p>
          </Modal>
        </Content>
      </Container>
    </>
  )
}

export default ResultSimulation
